import { useCallback, useEffect, useState } from "react";
import { format, parseISO, addDays } from "date-fns";
import { ChevronLeft, ChevronRight, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { btn } from "@/components/common/btn";
import { DateJump } from "@/components/common/DateJump";
import Segmented from "@/components/common/Segmented";
import { getTimesheetDayReview } from "@/lib/api";

const SEG_COLOR = { at_shop: "#64748b", at_site: "#10b981", en_route: "#0ea5e9", en_route_shop: "#38bdf8", home: "#cbd5e1" };
const SEG_LABEL = { at_shop: "Shop", at_site: "On site", en_route: "Driving", en_route_shop: "To shop" };
const BASE_START = 5, BASE_END = 19; // default timeline window (local hours), stretched to cover late/early work

const fmtT = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—");
const hourOf = (iso) => { const d = new Date(iso); return d.getHours() + d.getMinutes() / 60; };
const windowFor = (rows) => {
  let s = BASE_START, e = BASE_END;
  rows.forEach((r) => {
    [...(r.segments || []).flatMap((x) => [x.started_at, x.ended_at]), ...(r.manual_punches || []).flatMap((p) => [p.in_at, p.out_at])]
      .filter(Boolean).forEach((iso) => { const h = hourOf(iso); s = Math.min(s, Math.floor(h)); e = Math.max(e, Math.ceil(h) + 0.25); });
  });
  return [s, Math.min(24, e)];
};
const pctIn = (iso, [s, e]) => Math.min(100, Math.max(0, ((hourOf(iso) - s) / (e - s)) * 100));

// One tech's day: GPS segments as a bar, the manual punch window drawn as a bracket on top.
function Timeline({ row, win }) {
  const now = new Date().toISOString();
  const pct = (iso) => pctIn(iso, win);
  const ticks = []; for (let h = Math.ceil(win[0]) + 1; h < win[1]; h += 2) ticks.push(h);
  return (
    <div className="relative h-7 w-full min-w-[260px] rounded bg-slate-100" title="GPS segments (bar) vs manual punches (bracket)" data-testid={`day-timeline-${row.tech_id}`}>
      {(row.segments || []).map((s) => {
        const a = pct(s.started_at), b = pct(s.ended_at || now);
        return <div key={s.id || s.started_at} className="absolute top-2 h-3 rounded-sm" style={{ left: `${a}%`, width: `${Math.max(0.6, b - a)}%`, background: SEG_COLOR[s.state] || "#94a3b8" }}
          title={`${SEG_LABEL[s.state] || s.state} · ${fmtT(s.started_at)} – ${s.ended_at ? fmtT(s.ended_at) : "open"}${s.hours != null ? ` · ${s.hours}h` : ""}`} />;
      })}
      {(row.manual_punches || []).map((p) => {
        const a = pct(p.in_at), b = pct(p.out_at || now);
        return <div key={p.id} className="absolute top-0 h-full rounded border-x-2 border-t-2 border-slate-800/70 dark:border-white/70" style={{ left: `${a}%`, width: `${Math.max(0.6, b - a)}%` }}
          title={`Punch ${fmtT(p.in_at)} – ${p.out_at ? fmtT(p.out_at) : "open"}`} />;
      })}
      {ticks.map((h) => <span key={h} className="absolute bottom-0 text-[8px] text-slate-400 -translate-x-1/2" style={{ left: `${((h - win[0]) / (win[1] - win[0])) * 100}%` }}>{h > 12 ? h - 12 : h}{h >= 12 ? "p" : "a"}</span>)}
    </div>
  );
}

const Th = ({ children, right }) => <th className={`th-std whitespace-nowrap px-2 py-1.5 border-b border-slate-200 ${right ? "text-right" : "text-left"}`}>{children}</th>;
const Td = ({ children, right, className = "", testId }) => <td className={`px-2 py-1.5 border-b border-slate-100 text-xs ${right ? "text-right num" : ""} ${className}`} data-testid={testId}>{children}</td>;

export default function DayReviewPanel({ initialDate }) {
  const [date, setDate] = useState(initialDate || format(new Date(), "yyyy-MM-dd"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getTimesheetDayReview(date); setData(r.data); setError(""); }
    catch (e) { setError(e?.response?.data?.detail || "Could not load the day review"); }
    finally { setLoading(false); }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const rows = (data?.rows || []).filter((r) => filter === "all" ? true : filter === "variance" ? r.variance : filter === "active" ? (r.manual_hours || r.gps_hours || r.manual_open) : !(r.manual_hours || r.gps_hours));
  const isToday = date === format(new Date(), "yyyy-MM-dd");
  const win = windowFor(data?.rows || []);

  return (
    <div className="flex flex-col gap-2" data-testid="day-review-panel">
      <div className="flex flex-wrap items-center gap-2">
        <Segmented testId="day-review-filter" value={filter} onChange={setFilter} defaultKey="all"
          options={[["all", "All"], ["variance", "Variance"], ["active", "Worked"], ["none", "No time"]].map(([key, label]) => ({ key, label, testId: `day-filter-${key}` }))} />
        <span className="text-[11px] text-slate-500">GPS vs tech punches · variance over {data?.threshold_pct ?? 10}%</span>
        <div className="ml-auto flex items-center gap-1">
          <button type="button" className={`${btn("nav")} h-8 w-8 justify-center !px-0`} onClick={() => setDate(format(addDays(parseISO(date), -1), "yyyy-MM-dd"))} title="Previous day" data-testid="btn-day-prev"><ChevronLeft className="w-4 h-4" /></button>
          <button type="button" className={`${btn("nav")} h-8 !px-2`} onClick={() => setDate(format(new Date(), "yyyy-MM-dd"))} disabled={isToday} data-testid="btn-day-today">Today</button>
          <button type="button" className={`${btn("nav")} h-8 w-8 justify-center !px-0`} onClick={() => setDate(format(addDays(parseISO(date), 1), "yyyy-MM-dd"))} title="Next day" data-testid="btn-day-next"><ChevronRight className="w-4 h-4" /></button>
          <DateJump testId="day-review-date" value={parseISO(date)} onPick={(d) => setDate(format(d, "yyyy-MM-dd"))} label={format(parseISO(date), "EEE MMM d, yyyy")} />
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        </div>
      </div>
      {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="day-review-error">{error}</div>}
      <div className="data-table-container overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full border-collapse text-xs">
          <thead><tr>
            <Th>Tech</Th><Th>Clock in</Th><Th>Clock out</Th><Th right>Tech hrs</Th>
            <Th>GPS in</Th><Th>GPS out</Th><Th right>GPS hrs</Th><Th right>Δ</Th><Th>Day (GPS bar · punch bracket)</Th><Th>Review</Th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-slate-400">{loading ? "Loading…" : "No technicians for this filter."}</td></tr>}
            {rows.map((r) => (
              <tr key={r.tech_id} className="hover:bg-slate-50" data-testid={`day-row-${r.tech_id}`}>
                <Td><span className="inline-flex items-center gap-1.5 font-medium text-slate-800"><span className="h-2.5 w-2.5 rounded-full" style={{ background: r.tech_color }} />{r.tech_name}</span></Td>
                <Td>{fmtT(r.manual_clock_in_at)}</Td>
                <Td>{r.manual_open ? <span className="text-emerald-700 font-medium">on the clock</span> : fmtT(r.manual_clock_out_at)}</Td>
                <Td right testId={`day-tech-hrs-${r.tech_id}`}>{r.manual_hours ? r.manual_hours.toFixed(2) : r.tech_entered_hours ? <span title="Done for Day entry">{r.tech_entered_hours.toFixed(2)}*</span> : "—"}</Td>
                <Td>{fmtT(r.gps_clock_in_at)}</Td>
                <Td>{fmtT(r.gps_clock_out_at)}</Td>
                <Td right testId={`day-gps-hrs-${r.tech_id}`}>{r.gps_hours ? r.gps_hours.toFixed(2) : "—"}</Td>
                <Td right className={r.variance ? "text-red-600 font-semibold" : "text-slate-600"} testId={`day-delta-${r.tech_id}`}>{(r.manual_hours || r.gps_hours || r.tech_entered_hours) ? `${r.delta_hours > 0 ? "+" : ""}${r.delta_hours.toFixed(2)}` : "—"}</Td>
                <Td><Timeline row={r} win={win} /></Td>
                <Td testId={`day-review-${r.tech_id}`}>
                  {r.variance ? <span className="inline-flex items-center gap-1 text-red-600"><AlertTriangle className="w-3.5 h-3.5" />Variance{r.flagged_reason ? <span className="text-slate-400 font-normal"> · {r.flagged_reason}</span> : null}</span>
                    : (r.manual_hours || r.gps_hours) ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" />Matches</span>
                    : <span className="text-slate-400">—</span>}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-[10px] text-slate-500">
        {Object.entries(SEG_LABEL).map(([k, l]) => <span key={k} className="inline-flex items-center gap-1"><span className="h-2 w-3 rounded-sm" style={{ background: SEG_COLOR[k] }} />{l}</span>)}
        <span className="inline-flex items-center gap-1"><span className="h-2.5 w-3 border-x-2 border-t-2 border-slate-700" />Tech punch</span>
        <span>* hours from Done for Day when no punches</span>
      </div>
    </div>
  );
}
